import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@busbook/ui';
import { api } from './api.js';

/**
 * Minimal data hook. Every list screen needs the same four states — loading,
 * error, empty, loaded — so they are modelled once rather than per screen.
 */

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (value: T) => void;
}

export function useResource<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!path) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await api.get<T>(path));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this page.');
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, loading, reload: load, setData };
}

/** Turns any thrown value into a sentence worth showing a user. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof ApiError) {
    const details = error.details;
    if (Array.isArray(details) && details.length > 0) {
      const first = details[0] as { message?: string; path?: string };
      if (typeof first === 'string') return first;
      if (first?.message) return first.path ? `${first.path}: ${first.message}` : first.message;
    }
    return error.message;
  }
  return fallback;
}
